<script setup>
import { computed } from 'vue'
import { usePage, Link, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import BrandLockup from '@/Components/UI/BrandLockup.vue'
import Icon from '@/Components/UI/Icon.vue'

const props = defineProps({
  // supplier | vendor | host | driver
  portal: { type: String, default: 'supplier' },
})

// One shell, four personalities. Glyphs + tints match the Phase 2A auth shell;
// nav is per-portal (driver carries Earnings/Payouts).
const PORTALS = {
  supplier: {
    glyph: 'rental',
    tint: 'linear-gradient(145deg, var(--accent) 0%, color-mix(in oklch, var(--accent) 55%, var(--second)) 100%)',
    nav: [{ key: 'dashboard', href: '/' }, { key: 'profile', href: '/profile' }],
  },
  vendor: {
    glyph: 'compass',
    tint: 'linear-gradient(150deg, var(--accent) 0%, var(--second) 115%)',
    nav: [{ key: 'dashboard', href: '/' }, { key: 'profile', href: '/profile' }],
  },
  host: {
    glyph: 'home',
    tint: 'linear-gradient(155deg, var(--accent) 0%, color-mix(in oklch, var(--accent) 72%, var(--warn)) 100%)',
    nav: [{ key: 'dashboard', href: '/' }, { key: 'profile', href: '/profile' }],
  },
  driver: {
    glyph: 'truck',
    tint: 'linear-gradient(150deg, color-mix(in oklch, var(--accent) 62%, var(--warn)) 0%, var(--second) 120%)',
    nav: [{ key: 'dashboard', href: '/' }, { key: 'earnings', href: '/earnings' }, { key: 'payouts', href: '/payouts' }, { key: 'profile', href: '/profile' }],
  },
}

const cfg = computed(() => PORTALS[props.portal] || PORTALS.supplier)

const page = usePage()
const { t } = useI18n()
const flash = computed(() => page.props.flash || {})
const currentLocale = computed(() => page.props.locale || 'en')
const ownerName = computed(() => page.props.portal?.owner_name || '')
const path = computed(() => (page.url || '/').split('?')[0])

function isActive(href) {
  return href === '/' ? path.value === '/' : path.value.startsWith(href)
}
function setLang(lang) {
  if (lang === currentLocale.value) return
  router.get(window.location.pathname, { lang }, { preserveScroll: true, preserveState: false })
}
</script>

<template>
  <div class="psh">
    <header
      class="psh__band"
      :style="{ background: cfg.tint }"
    >
      <div class="psh__bar">
        <span class="psh__lock"><BrandLockup /></span>
        <nav class="psh__nav">
          <Link
            v-for="item in cfg.nav"
            :key="item.key"
            :href="item.href"
            :class="['psh__pill', isActive(item.href) && 'is-active']"
            :aria-current="isActive(item.href) ? 'page' : undefined"
          >{{ t(`${portal}_portal.nav.${item.key}`) }}</Link>
        </nav>
        <div class="psh__right">
          <div class="psh__lang">
            <button
              :class="['psh__lang-btn', currentLocale === 'en' && 'is-active']"
              type="button"
              @click="setLang('en')"
            >EN</button>
            <span class="psh__lang-sep">·</span>
            <button
              :class="['psh__lang-btn', currentLocale === 'ru' && 'is-active']"
              type="button"
              @click="setLang('ru')"
            >RU</button>
          </div>
          <Link
            href="/logout"
            method="delete"
            as="button"
            class="psh__logout"
          >{{ t(`${portal}_portal.nav.sign_out`) }}</Link>
        </div>
      </div>

      <div class="psh__greet">
        <Icon
          :name="cfg.glyph"
          :size="170"
          :stroke="1.4"
          class="psh__glyph"
        />
        <p class="psh__eyebrow">{{ t(`${portal}_portal.brand.sub`) }}</p>
        <h1 class="psh__welcome">
          {{ t('portal_chrome.welcome') }}<template v-if="ownerName">, {{ ownerName }}</template>
        </h1>
      </div>
    </header>

    <div
      v-if="flash.alert"
      class="psh__flash psh__flash--alert"
      role="alert"
    >{{ flash.alert }}</div>
    <div
      v-if="flash.notice"
      class="psh__flash psh__flash--notice"
    >{{ flash.notice }}</div>

    <main class="psh__main"><slot /></main>
  </div>
</template>

<style scoped>
.psh { min-height: 100vh; background: var(--bg); color: var(--ink); }

/* Gradient band */
.psh__band { position: relative; overflow: hidden; color: var(--accentInk); padding-top: max(env(safe-area-inset-top), 0); }
.psh__bar { position: relative; z-index: 2; max-width: 72rem; margin: 0 auto; padding: 0.85rem clamp(1rem, 4vw, 1.75rem); display: flex; align-items: center; gap: 1rem 1.25rem; flex-wrap: wrap; }
.psh__lock :deep(.brand-lockup__sun) { color: var(--accentInk) !important; }
.psh__lock :deep(.brand-lockup__word) { color: var(--accentInk) !important; }
.psh__nav { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; }
.psh__pill { display: inline-flex; align-items: center; padding: 0.4rem 0.9rem; border-radius: 999px; font-size: 0.875rem; font-weight: 600; text-decoration: none; color: color-mix(in oklch, var(--accentInk) 82%, transparent); transition: background 150ms ease, color 150ms ease; }
.psh__pill:hover { background: color-mix(in oklch, var(--accentInk) 14%, transparent); color: var(--accentInk); }
.psh__pill.is-active { background: var(--accentInk); color: var(--accent); }
.psh__right { margin-left: auto; display: flex; align-items: center; gap: 1rem; }
.psh__lang { display: flex; align-items: center; gap: 0.25rem; }
.psh__lang-btn { background: transparent; border: 0; color: color-mix(in oklch, var(--accentInk) 65%, transparent); font-size: 0.8125rem; font-weight: 600; cursor: pointer; padding: 0.1rem 0.2rem; letter-spacing: 0.03em; }
.psh__lang-btn.is-active { color: var(--accentInk); }
.psh__lang-sep { color: color-mix(in oklch, var(--accentInk) 45%, transparent); font-size: 0.75rem; }
.psh__logout { background: color-mix(in oklch, var(--accentInk) 14%, transparent); border: 1px solid color-mix(in oklch, var(--accentInk) 40%, transparent); color: var(--accentInk); padding: 0.4rem 0.85rem; border-radius: var(--radius-button, 8px); font-size: 0.875rem; font-weight: 600; cursor: pointer; }
.psh__logout:hover { background: color-mix(in oklch, var(--accentInk) 22%, transparent); }

/* Greeting strip */
.psh__greet { position: relative; z-index: 2; max-width: 72rem; margin: 0 auto; padding: 0.5rem clamp(1rem, 4vw, 1.75rem) clamp(1.25rem, 3vw, 1.75rem); }
.psh__glyph { position: absolute; right: clamp(-2rem, 2vw, 0rem); top: 50%; transform: translateY(-50%); color: var(--accentInk); opacity: 0.15; pointer-events: none; z-index: -1; }
.psh__eyebrow { font-size: 0.75rem; letter-spacing: 0.16em; text-transform: uppercase; font-weight: 700; opacity: 0.85; margin: 0 0 0.2rem; }
.psh__welcome { font-family: var(--display); font-weight: 800; font-size: clamp(1.4rem, 3vw, 1.9rem); line-height: 1.15; letter-spacing: -0.02em; margin: 0; }

/* Flash */
.psh__flash { max-width: 72rem; margin: 1rem auto 0; width: calc(100% - 2.5rem); padding: 0.75rem 1rem; border-radius: var(--radius-input, 0.5rem); font-size: 0.9375rem; }
.psh__flash--alert { background: color-mix(in oklch, var(--danger) 12%, var(--surface)); color: var(--danger); }
.psh__flash--notice { background: color-mix(in oklch, var(--ok) 12%, var(--surface)); color: var(--ok); }

/* Content */
.psh__main { max-width: 72rem; margin: 0 auto; padding: 1.5rem clamp(1rem, 4vw, 1.75rem) 3rem; }
</style>
